import { Chip, Stack } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import React from "react";

/**
 * **Chip di salute SLA** (totale, in tempo, a rischio, violati): la resa
 * condivisa fra l'intestazione del Monitor (`SlaLiveKpiChips`, conteggi KPI
 * dell'organizzazione) e la pagina Le mie attivita' (conteggi dei task in
 * lista). Solo presentazione: conteggi e filtro arrivano dal chiamante.
 *
 * Regole:
 * - "Totale" e' il chip di "nessun filtro": selezionato quando la salute
 *   filtrata e' assente;
 * - in tempo / a rischio / violati sono interruttori: un clic seleziona, un
 *   secondo clic (o "Totale") toglie il filtro;
 * - conteggio assente (non ancora letto) reso come "—";
 * - chip pieno se selezionato, contornato altrimenti; pulsanti (`aria-pressed`)
 *   raggiungibili da tastiera.
 */
export interface SlaHealthFilterCounts {
  total: number | undefined;
  on_time: number | undefined;
  at_risk: number | undefined;
  breached: number | undefined;
}

interface SlaHealthFilterChipsProps {
  counts: SlaHealthFilterCounts | null;
  /** Salute filtrata (0, 1, 2) o `null` senza filtro. */
  selectedHealth: number | null;
  /** Nuovo filtro: `null` lo toglie. */
  onSelectHealth: (health: number | null) => void;
  /** Radice dei `data-testid` (gruppo e `<radice>-<chip>`). */
  testIdPrefix: string;
  /** Conteggi non ancora letti: `aria-busy` sul gruppo. */
  busy?: boolean;
  /** Attributi `data-*` aggiuntivi del gruppo (es. `data-kpi-as-of`). */
  dataAttributes?: Record<`data-${string}`, string>;
  sx?: React.ComponentProps<typeof Stack>["sx"];
}

const SlaHealthFilterChips: React.FC<SlaHealthFilterChipsProps> = ({
  counts,
  selectedHealth,
  onSelectHealth,
  testIdPrefix,
  busy = false,
  dataAttributes,
  sx,
}) => {
  const translations = useTranslations();

  const chips: Array<{
    id: string;
    /** Salute filtrata dal chip; `null` per "Totale" (toglie il filtro). */
    health: number | null;
    labelKey: string;
    count: number | undefined;
    color: "success" | "warning" | "error" | "default";
  }> = [
    { id: "total", health: null, labelKey: SLA_MONITOR_TEXT_KEYS.kpiTotal, count: counts?.total, color: "default" },
    { id: "on-time", health: 0, labelKey: SLA_MONITOR_TEXT_KEYS.kpiOnTime, count: counts?.on_time, color: "success" },
    { id: "at-risk", health: 1, labelKey: SLA_MONITOR_TEXT_KEYS.kpiAtRisk, count: counts?.at_risk, color: "warning" },
    { id: "breached", health: 2, labelKey: SLA_MONITOR_TEXT_KEYS.kpiBreached, count: counts?.breached, color: "error" },
  ];

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      role="group"
      data-testid={testIdPrefix}
      {...dataAttributes}
      aria-busy={busy}
      sx={sx}
    >
      {chips.map((chip) => {
        const pressed =
          chip.health === null ? selectedHealth == null : chip.health === selectedHealth;
        return (
          <Chip
            key={chip.id}
            size="medium"
            color={chip.color}
            variant={pressed ? "filled" : "outlined"}
            data-testid={`${testIdPrefix}-${chip.id}`}
            aria-pressed={pressed}
            // MUI rende il chip cliccabile un role="button" con tabIndex 0:
            // Invio e Spazio lo attivano.
            onClick={() =>
              onSelectHealth(chip.health === null || pressed ? null : chip.health)
            }
            label={`${getTranslation(translations, chip.labelKey)} ${
              typeof chip.count === "number" ? chip.count : "—"
            }`}
            sx={{ height: 30, fontSize: 14, fontWeight: 700 }}
          />
        );
      })}
    </Stack>
  );
};

export default SlaHealthFilterChips;
